import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {Background, ReactFlow, ReactFlowProvider} from 'reactflow';

import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {capitalize} from 'util/NameUtil';

import {Transition} from '../../types/transition/TransitionTypes';

export enum TransitionModalAction {
  EDIT = 'EDIT',
  VIEW = 'VIEW',
  ADD = 'ADD',
}

export enum TransitionType {
  PLANNED = 'PLANNED',
  OPTIONAL = 'OPTIONAL',
}

interface TransitionMode {
  action: TransitionModalAction;
  type: TransitionType;
}

export interface SingleFlowchartTransitionModalProps {
  action: TransitionModalAction;
  /** The transition to view/edit, undefined for add mode. */
  transition?: Transition;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

function getModalTitle(mode: TransitionMode): string {
  const {action, type} = mode;
  const title = capitalize(action);
  return `${title} ${type.toLowerCase()} transition`;
}

function getInfoMessage(mode: TransitionMode): string | null {
  const {action, type} = mode;
  if (action === TransitionModalAction.EDIT && type === TransitionType.PLANNED) {
    return 'Saving edits to planned transitions updates the main flowchart.';
  }
  return null;
}

function TransitionModalContent(): JSX.Element {
  return (
    <div className={css(styles.contentContainer)}>
      <div className={css(styles.canvasContainer)}>
        {/* TODO: Replace with flowchart editor component - stubbing for visual testing */}
        <ReactFlow nodes={[]} edges={[]} fitView={true} proOptions={{hideAttribution: true}}>
          <Background gap={16} />
        </ReactFlow>
      </div>
    </div>
  );
}

function TransitionModalFooterInfo(props: {mode: TransitionMode}): JSX.Element | null {
  const infoMessage = getInfoMessage(props.mode);
  if (!infoMessage) {
    return null;
  }

  return (
    <div className={css(styles.footerInfo)} role="status">
      <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
      <span className={css(styles.infoText)}>{infoMessage}</span>
    </div>
  );
}

/**
 * A modal for editing or viewing a single flowchart transition.
 *
 * Supports default & optional transitions, and merging & single node transitions.
 */
export default function SingleFlowchartTransitionModal(
  props: SingleFlowchartTransitionModalProps
): JSX.Element {
  const {action, transition, onRequestClose, onRequestSubmit} = props;
  const type = transition?.isPlanned === true ? TransitionType.PLANNED : TransitionType.OPTIONAL;
  const mode: TransitionMode = {action, type};
  const isViewMode = action === TransitionModalAction.VIEW;
  const buttons = isViewMode
    ? [
        {
          text: 'Close',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: onRequestClose,
        },
      ]
    : [
        {
          text: 'Cancel',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: onRequestClose,
        },
        {
          text: 'Save',
          style: ButtonStyle.PRIMARY,
          submit: true,
        },
      ];

  return (
    <Modal
      title={getModalTitle(mode)}
      extraLarge={true}
      fullHeight={true}
      bodyPadded={false}
      flexContent={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={isViewMode ? undefined : onRequestSubmit}
      footer={<TransitionModalFooterInfo mode={{action, type}} />}
      buttons={buttons}
      dataTestAttrs={{component: 'TransitionModal', element: 'root'}}
    >
      <ReactFlowProvider>
        <TransitionModalContent />
      </ReactFlowProvider>
    </Modal>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    display: 'flex',
    flex: 1,
    height: '100%',
    minHeight: 0,
  },
  canvasContainer: {
    flex: 1,
    backgroundColor: Color.GRAY_50,
    position: 'relative',
  },
  footerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '36px',
  },
  infoIcon: {
    color: Color.GRAY_600,
    fontSize: '14px',
  },
  infoText: {
    fontSize: '14px',
    color: Color.GRAY_900,
  },
});
